// ---- Rig-measured wear seating ----
// ONE home for "where do shoulders / neck / yoke / hood / sleeves / the whole
// body envelope sit?" on a built humanoid. Coded wear (cloaks) and check-wear
// MUST use this — never invent YOKE_Y / hood apex / sleeve radii / chest or
// thigh radii. Sex/build change arm thickness, shoulder width and every limb
// girth; freehand 0.078 armholes poke through male arms, freehand greave
// tubes poke on stout legs.
//
// Coordinate spaces:
//   TORSO-LOCAL    — torso limb pivot = hip (yoke / body shell / chest+hip).
//   HEAD-LOCAL     — head limb pivot = neck (hood dome).
//   SHOULDER-LOCAL — shoulderL/R pivot; arm hangs −Y (sleeves).
//   HIP-LOCAL      — hipL/R pivot; thigh hangs −Y (thigh envelope, kneeY).
//   KNEE-LOCAL     — kneeL/R pivot; shin hangs −Y (shin/ankle/boot envelope).
//
// The AUTHORING BASELINE (what wear-filler docs size against) is the formula
// table in src/model/wear-scale.ts (wearBodyRadii / AVERAGE_MALE_BODY_RADII) —
// the same numbers the fill-time WearScaleCtx multiplies. wearBodyFromRig
// below MEASURES a live rig; the seat tests drift-guard the two against each
// other so formula, fill, and check can never disagree silently.

import type * as THREE from "three";
import { HUMANOID_TUNING, scalpToHeadLocal } from "../model/humanoid.ts";
import { humanoidCrownFrame } from "../model/head-seat.ts";
import { AVERAGE_MALE_BODY_RADII, CHEST_BAND_NOMINAL, HIP_BAND_NOMINAL } from "../model/wear-scale.ts";

/** How far the tunic lathe tops out above the head pivot (HUMANOID_TUNING
 *  profile end ~1.69 vs head at shoulderY−0.02·s → ≈0.08 at s=1.1). */
export const TORSO_TOP_ABOVE_NECK = 0.08;

/** Yoke blend: bias hard toward the tunic top so the collar rests on the
 *  shoulder mass (not collarbone mid-chest, not floating up the neck). */
export const YOKE_SHELF_W = 0.15;
export const YOKE_TOP_W = 0.85;

/**
 * Pinned average-male (body scale 1.1) torso-local seats — parity guard.
 * Re-derive if humanoid PELVIS_HIP_Y / shoulder chain changes.
 */
export const AVERAGE_MALE_SEAT = {
  shelfY: 0.5775,
  neckY: 0.616,
  torsoTop: 0.696,
  /** shelf*0.15 + torsoTop*0.85 */
  yokeY: 0.678225,
} as const;

export type WearSeat = {
  /** Deltoid / shoulder-joint y (torso-local). */
  shelfY: number;
  /** Head pivot = neck base (torso-local). */
  neckY: number;
  /** Estimated tunic top (neckY + TORSO_TOP_ABOVE_NECK). */
  torsoTop: number;
  /** Recommended coat/robe neck-opening y. */
  yokeY: number;
};

function findLimb(root: THREE.Object3D, name: string): THREE.Object3D | null {
  let found: THREE.Object3D | null = null;
  root.traverse((o) => {
    if (o.userData?.limb === name) found = o;
  });
  return found;
}

/** Measure seating landmarks from a built humanoid rig (player/NPC group). */
export function wearSeatFromRig(root: THREE.Object3D): WearSeat {
  const sh = findLimb(root, "shoulderL") ?? findLimb(root, "shoulderR");
  const head = findLimb(root, "head");
  const shelfY = sh?.position.y ?? AVERAGE_MALE_SEAT.shelfY;
  const neckY = head?.position.y ?? shelfY + 0.04;
  const torsoTop = neckY + TORSO_TOP_ABOVE_NECK;
  const yokeY = shelfY * YOKE_SHELF_W + torsoTop * YOKE_TOP_W;
  return { shelfY, neckY, torsoTop, yokeY };
}

/** Alias kept for cloak call sites / external docs. */
export function yokeFromRig(root: THREE.Object3D): number {
  return wearSeatFromRig(root).yokeY;
}

// ---- Hood seat (HEAD-LOCAL) -----------------------------------------------
// Derived from humanoidCrownFrame + HUMANOID_TUNING.scalp (same math as
// head-seat.ts for prim helmets). A freehand hood apex of 0.42 sat UNDER the
// skull top (~0.47) and never covered the head — that failure is pinned below.

/** Cloth clearance of hood tip above the egg apex (head-local). */
export const HOOD_CLEARANCE = 0.07;
/** Shell radius as a multiple of skull lathe r (cloth over the egg). */
export const HOOD_SHELL_MUL = 1.18;
/** Apex may sit this far below expected before WARN (under-skull is always WARN). */
export const HOOD_APEX_TOL = 0.03;
/** Shell r may be this fraction under headR before WARN. */
export const HOOD_SHELL_MIN_MUL = 1.02;

export type HoodSeat = {
  /** Egg apex y in head-local (from headProfile top / measured mesh). */
  skullTopY: number;
  /** Scalp crown landmark y (head-local). */
  crownY: number;
  /** Ear mid y — side of face opening / brim. */
  earY: number;
  /** Low nape y — rear of cowl. */
  napeY: number;
  /** Front hairline y — brow band. */
  hairlineY: number;
  /** Occipital y — back of skull (nape pad seat). */
  occipitalY: number;
  /** Horizontal skull radius (lathe max r × scale). */
  headR: number;
  /** Recommended dome tip y (= skullTop + HOOD_CLEARANCE). */
  apexY: number;
  /** Recommended outer shell radius (= headR × HOOD_SHELL_MUL). */
  shellR: number;
  /** Effective humanoid scale (includes the 1.1 base). */
  scale: number;
};

/** Head-local hood landmarks from the same crown frame helmets use. */
export function hoodSeatFromScale(bodyScale = 1): HoodSeat {
  const frame = humanoidCrownFrame({ scale: bodyScale });
  const s = frame.scale;
  // headY = headPivot + 0.25·s  (see scalpToHeadLocal / humanoid builder).
  const headPivotY = frame.headY - 0.25 * s;
  const skullTopY = frame.skullTopY - headPivotY;
  const crownY = scalpToHeadLocal(HUMANOID_TUNING.scalp.crown, s)[1];
  const earY = scalpToHeadLocal(HUMANOID_TUNING.scalp.earL, s)[1];
  const napeY = scalpToHeadLocal(HUMANOID_TUNING.scalp.nape, s)[1];
  const hairlineY = scalpToHeadLocal(HUMANOID_TUNING.scalp.hairline, s)[1];
  const occipitalY = scalpToHeadLocal(HUMANOID_TUNING.scalp.occipital, s)[1];
  const headR = frame.headR;
  return {
    skullTopY,
    crownY,
    earY,
    napeY,
    hairlineY,
    occipitalY,
    headR,
    apexY: skullTopY + HOOD_CLEARANCE,
    shellR: headR * HOOD_SHELL_MUL,
    scale: s,
  };
}

/**
 * Pinned average-male head-local hood seat — parity guard.
 * s=1.1: skullTop≈0.4675, crown≈0.484, headR≈0.1705.
 */
export const AVERAGE_MALE_HOOD: HoodSeat = hoodSeatFromScale(1);

/**
 * Prefer live head mesh bounds when the rig is already built (sex/build safe);
 * fall back to crown-frame math if the head has no measurables yet.
 */
export function hoodSeatFromRig(root: THREE.Object3D): HoodSeat {
  const formula = hoodSeatFromScale(1);
  const head = findLimb(root, "head");
  if (!head) return formula;

  // Measure non-coded meshes under the head limb in HEAD-LOCAL space.
  head.updateWorldMatrix(true, true);
  const inv = head.matrixWorld.clone().invert();
  const v = { x: 0, y: 0, z: 0 };
  let maxY = -Infinity;
  let maxR = 0;
  let any = false;
  head.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || m.userData?.codedWear) return;
    const pos = m.geometry?.getAttribute?.("position");
    if (!pos) return;
    const toHead = inv.clone().multiply(m.matrixWorld);
    for (let i = 0; i < pos.count; i++) {
      v.x = pos.getX(i);
      v.y = pos.getY(i);
      v.z = pos.getZ(i);
      // applyMatrix4 inline (avoid THREE import dependency for Vector3)
      const e = toHead.elements;
      const x = v.x * e[0]! + v.y * e[4]! + v.z * e[8]! + e[12]!;
      const y = v.x * e[1]! + v.y * e[5]! + v.z * e[9]! + e[13]!;
      const z = v.x * e[2]! + v.y * e[6]! + v.z * e[10]! + e[14]!;
      any = true;
      if (y > maxY) maxY = y;
      const r = Math.hypot(x, z);
      if (r > maxR) maxR = r;
    }
  });

  if (!any || !(maxY > 0) || !(maxR > 0)) return formula;

  // Keep scalp proportions from the formula; replace skull envelope with live measure.
  const skullTopY = maxY;
  const headR = Math.max(formula.headR, maxR * 0.92); // lathe r ≈ envelope * depth-oval
  return {
    ...formula,
    skullTopY,
    headR,
    // Preserve relative scalp offsets from formula when mesh is taller/shorter.
    apexY: skullTopY + HOOD_CLEARANCE,
    shellR: headR * HOOD_SHELL_MUL,
  };
}

/**
 * Hood dome must clear the skull. Classic freehand apex 0.42 on average male
 * (skullTop≈0.47) is the pinned under-skull failure.
 */
export function hoodSeatingWarns(
  seat: HoodSeat,
  actualApexY: number,
  actualShellR: number,
): string[] {
  const warns: string[] = [];
  if (actualApexY < seat.skullTopY) {
    warns.push(
      `hood apex y=${actualApexY.toFixed(3)} is UNDER skull top ${seat.skullTopY.toFixed(3)} — ` +
        `dome never covers the head (classic freehand 0.42 failure; use hoodSeatFromRig)`,
    );
  } else if (actualApexY < seat.apexY - HOOD_APEX_TOL) {
    warns.push(
      `hood apex y=${actualApexY.toFixed(3)} is short of expected ${seat.apexY.toFixed(3)} ` +
        `(skullTop=${seat.skullTopY.toFixed(3)} + clearance ${HOOD_CLEARANCE})`,
    );
  }
  const minR = seat.headR * HOOD_SHELL_MIN_MUL;
  if (actualShellR < minR) {
    warns.push(
      `hood shell r=${actualShellR.toFixed(3)} < headR×${HOOD_SHELL_MIN_MUL}=${minR.toFixed(3)} ` +
        `(headR=${seat.headR.toFixed(3)}) — dome will clip the egg`,
    );
  }
  return warns;
}

// ---- Seating WARN thresholds (check-wear / unit tests) --------------------

/** |actualYoke − expectedYoke| above this → WARN (units = torso-local). */
export const YOKE_TOL = 0.035;
/** Actual yoke above torsoTop by more than this → floating on the neck. */
export const YOKE_ABOVE_TOP = 0.02;
/** Actual yoke below shelf by more than this → sagging mid-chest. */
export const YOKE_BELOW_SHELF = 0.02;

/**
 * Compare an authored/attached yoke height against the live seat.
 * Returns WARN strings (empty = ok).
 */
export function seatingWarns(seat: WearSeat, actualYokeY: number): string[] {
  const warns: string[] = [];
  const { shelfY, neckY, torsoTop, yokeY: expected } = seat;
  const d = actualYokeY - expected;
  if (Math.abs(d) > YOKE_TOL) {
    warns.push(
      `yoke y=${actualYokeY.toFixed(3)} is ${d > 0 ? "+" : ""}${d.toFixed(3)} from ` +
        `rig-expected ${expected.toFixed(3)} (shelf=${shelfY.toFixed(3)} neck=${neckY.toFixed(3)} ` +
        `torsoTop=${torsoTop.toFixed(3)}) — measure with wearSeatFromRig, don't hardcode`,
    );
  }
  if (actualYokeY > torsoTop + YOKE_ABOVE_TOP) {
    warns.push(
      `yoke y=${actualYokeY.toFixed(3)} is above tunic top ${torsoTop.toFixed(3)} — ` +
        `collar will float on the neck (classic hardcoded 0.72 failure)`,
    );
  }
  if (actualYokeY < shelfY - YOKE_BELOW_SHELF) {
    warns.push(
      `yoke y=${actualYokeY.toFixed(3)} is below shoulder shelf ${shelfY.toFixed(3)} — ` +
        `collar sits mid-chest (classic 0.62 collarbone failure)`,
    );
  }
  return warns;
}

// ---- Sleeve seat (SHOULDER-LOCAL) -----------------------------------------
// Arms are thicker on male than female (armScale 1 vs 0.86). Measure the live
// arm mesh under shoulderL/R — never freehand r≈0.078 (pokes male deltoids).

/** Cloth outside the measured arm envelope. */
export const SLEEVE_CLEARANCE = 0.02;
/** Extra on the deltoid pad so the blue shirt never flashes at the armhole. */
export const SLEEVE_PAD_CLEARANCE = 0.028;
/** Min sleeveR / armR before WARN (poke-through). */
export const SLEEVE_MIN_MUL = 1.05;

export type SleeveSeat = {
  /** Max arm mesh radius in shoulder-local XZ (includes deltoid fat top). */
  armR: number;
  /** Elbow joint y (shoulder-local, negative — arm hangs −Y). */
  elbowY: number;
  /** Approximate fist / hand y (end of arm mesh). */
  fistY: number;
  /** Highest arm mesh y above the joint (deltoid top). */
  deltoidTopY: number;
  /** Outer sleeve radius at the upper arm (= armR + SLEEVE_CLEARANCE). */
  sleeveR: number;
  /** Shoulder-cap outer radius. */
  padR: number;
  /** Armhole / pad peak y (above joint). */
  armholeY: number;
  /** Cuff y (hand still clears). */
  cuffY: number;
};

/** Fallback when no arm mesh is measurable (matches average male s=1.1). */
export const AVERAGE_MALE_SLEEVE: SleeveSeat = {
  armR: 0.101,
  elbowY: -0.347,
  fistY: -0.82,
  deltoidTopY: 0.101,
  sleeveR: 0.101 + SLEEVE_CLEARANCE,
  padR: 0.101 + SLEEVE_PAD_CLEARANCE,
  armholeY: 0.12,
  cuffY: -0.72,
};

function measureArmInShoulderLocal(
  shoulder: THREE.Object3D,
): { armR: number; minY: number; maxY: number } | null {
  shoulder.updateWorldMatrix(true, true);
  const inv = shoulder.matrixWorld.clone().invert();
  let armR = 0;
  let minY = Infinity;
  let maxY = -Infinity;
  let any = false;
  shoulder.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || m.userData?.codedWear) return;
    const pos = m.geometry?.getAttribute?.("position");
    if (!pos) return;
    const toLocal = inv.clone().multiply(m.matrixWorld);
    const e = toLocal.elements;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);
      const X = x * e[0]! + y * e[4]! + z * e[8]! + e[12]!;
      const Y = x * e[1]! + y * e[5]! + z * e[9]! + e[13]!;
      const Z = x * e[2]! + y * e[6]! + z * e[10]! + e[14]!;
      any = true;
      const r = Math.hypot(X, Z);
      if (r > armR) armR = r;
      if (Y < minY) minY = Y;
      if (Y > maxY) maxY = Y;
    }
  });
  if (!any || !(armR > 0)) return null;
  return { armR, minY, maxY };
}

/**
 * Measure sleeve seating from the live arm under shoulderL/R.
 * Takes the WORSE (thicker) arm so male + female both clear.
 */
export function sleeveSeatFromRig(root: THREE.Object3D): SleeveSeat {
  const shL = findLimb(root, "shoulderL");
  const shR = findLimb(root, "shoulderR");
  const elbows = [
    findLimb(root, "elbowL"),
    findLimb(root, "elbowR"),
  ].filter(Boolean) as THREE.Object3D[];

  let armR = 0;
  let minY = 0;
  let maxY = 0;
  let measured = false;
  for (const sh of [shL, shR]) {
    if (!sh) continue;
    const m = measureArmInShoulderLocal(sh);
    if (!m) continue;
    measured = true;
    if (m.armR > armR) {
      armR = m.armR;
      minY = m.minY;
      maxY = m.maxY;
    } else if (m.armR === armR) {
      minY = Math.min(minY, m.minY);
      maxY = Math.max(maxY, m.maxY);
    }
  }
  if (!measured) return { ...AVERAGE_MALE_SLEEVE };

  // Elbow y: shoulder-local position of the elbow limb (parented under shoulder).
  // matrixWorld.elements[13] is world Y of the pivot origin after transform —
  // convert via inv(shoulder) * elbow.worldPosition.
  let elbowY = AVERAGE_MALE_SLEEVE.elbowY;
  if (elbows.length && (shL || shR)) {
    const sh = shL ?? shR!;
    sh.updateWorldMatrix(true, true);
    const inv = sh.matrixWorld.clone().invert();
    let ey = 0;
    let n = 0;
    for (const el of elbows) {
      el.updateWorldMatrix(true, false);
      // Elbow pivot is at local origin → world = matrixWorld translation column.
      const we = el.matrixWorld.elements;
      const wx = we[12]!;
      const wy = we[13]!;
      const wz = we[14]!;
      const e = inv.elements;
      const Y = wx * e[1]! + wy * e[5]! + wz * e[9]! + e[13]!;
      ey += Y;
      n++;
    }
    if (n) elbowY = ey / n;
  }

  const fistY = minY;
  const deltoidTopY = Math.max(0.04, maxY);
  const sleeveR = armR + SLEEVE_CLEARANCE;
  const padR = armR + SLEEVE_PAD_CLEARANCE;
  // Cuff sits short of the fist so the hand still clears.
  const cuffY = elbowY + (fistY - elbowY) * 0.78;
  const armholeY = deltoidTopY * 1.15;

  return {
    armR,
    elbowY,
    fistY,
    deltoidTopY,
    sleeveR,
    padR,
    armholeY,
    cuffY,
  };
}

/**
 * Sleeve outer must clear the measured arm. Freehand r=0.078 on male armR≈0.10
 * is the pinned poke-through failure.
 */
export function sleeveSeatingWarns(seat: SleeveSeat, actualSleeveR: number): string[] {
  const warns: string[] = [];
  const minR = seat.armR * SLEEVE_MIN_MUL;
  if (actualSleeveR < minR) {
    warns.push(
      `sleeve r=${actualSleeveR.toFixed(3)} < armR×${SLEEVE_MIN_MUL}=${minR.toFixed(3)} ` +
        `(armR=${seat.armR.toFixed(3)}) — arm will poke through (classic freehand 0.078 on male)`,
    );
  }
  if (actualSleeveR < seat.armR) {
    warns.push(
      `sleeve r=${actualSleeveR.toFixed(3)} is UNDER measured armR ${seat.armR.toFixed(3)} — ` +
        `use sleeveSeatFromRig, don't hardcode`,
    );
  }
  return warns;
}

// ---- Whole-body envelope (TORSO / HIP / KNEE local) ------------------------
// The full touch-point graph below the neck: chest/hip shell radii, thigh/
// shin/ankle girths, boot extent. Measured live from the rig (sex/build
// safe); regions with no measurable meshes (skirt builds have no leg limbs)
// fall back to the formula baseline (wear-scale.ts). The wear-check envelope
// WARNs compare authored filler radii against these × the MIN_MULs.

/** Min shell r / chestR (or hipR) before WARN. */
export const TORSO_SHELL_MIN_MUL = 1.02;
/** Min front-z extent / chestZ before WARN (the female-bust catcher). */
export const TORSO_FRONT_MIN_MUL = 1.0;
/** Min greave/chausse r / thighR (or shinR) before WARN. */
export const LEG_SHELL_MIN_MUL = 1.03;
/** Min boot-cuff r / ankleR before WARN. */
export const FOOT_CUFF_MIN_MUL = 1.02;
/** Min covering shoulder-pad r / armR before WARN. Pads sit ON the deltoid
 *  (coverage from above, not a wrapping tube), so arm-scale suffices — the
 *  classic freehand 0.078 pad still fails, a snug 0.1 cap passes. */
export const SHOULDER_PAD_MIN_MUL = 0.95;
/** A leg/foot prim is a WRAP CANDIDATE when its OWN radius covers this
 *  fraction of the band body radius — studs/gems/buckles (r ≈ ¼ body) can
 *  never be wraps no matter where they sit. Candidates are then judged by
 *  RADIAL REACH (|axis offset| + radius) against the MIN_MUL: a centered
 *  under-sized tube stays judged all the way down to this floor (no 0.84×
 *  escape hatch), while a proud surface piece (quilt puff, knee cop) earns
 *  its clearance from the protrusion. */
export const WRAP_JUDGE_FRACTION = 0.5;
/** A BOX leg/foot prim is a wrap shell only when its SMALLER half-extent
 *  covers this fraction of the band body radius — straps/soles/buckles have
 *  one thin dimension and are exempt; a genuine box-plate shell is judged. */
export const BOX_WRAP_JUDGE_FRACTION = 0.7;
/** A back-slot part WRAPS the torso (and is judged as a torso shell) when its
 *  front-z reach in back-anchor-local space passes this ×sEff — the nominal
 *  full torso depth (the back anchor sits ON the back surface, 2×0.135 from
 *  the front). Hanging curtains/packs stay behind and are exempt. */
export const BACK_WRAP_MIN_Z = 0.27;

export type WearBodySeat = {
  /** The existing three families, re-measured on this rig. */
  yoke: WearSeat;
  hood: HoodSeat;
  sleeve: SleeveSeat;
  /** Max torso radius in the chest band (TORSO-local). */
  chestR: number;
  /** Front (+z) extent in the chest band — bust included on female. */
  chestZ: number;
  /** Max torso radius in the hip band. */
  hipR: number;
  /** Max thigh radius (HIP-local, worse side). */
  thighR: number;
  /** Knee pivot y (HIP-local, negative). */
  kneeY: number;
  /** Max shin radius above the ankle (KNEE-local). */
  shinR: number;
  /** Ankle y (KNEE-local, negative). */
  ankleY: number;
  /** Shin/ankle radius at the boot cuff band. */
  ankleR: number;
  /** Boot heel→toe z extent. */
  footLen: number;
  /** Effective body scale, from the measured shelf (shelfY = 0.525·s on
   *  every build) — consumers derive band placements from this instead of
   *  re-deriving the identity. */
  sEff: number;
};

/** Sweep a limb's meshes into limb-local space, visiting every vertex.
 *  Stops at nested limb children (shoulders/head under torso, knee under hip)
 *  and skips coded-wear meshes — same discipline as measureArmInShoulderLocal. */
function sweepLimbLocal(
  limb: THREE.Object3D,
  visit: (x: number, y: number, z: number) => void,
): boolean {
  limb.updateWorldMatrix(true, true);
  const inv = limb.matrixWorld.clone().invert();
  let any = false;
  const walk = (o: THREE.Object3D) => {
    if (o !== limb && (o as { userData?: { limb?: string } }).userData?.limb) return;
    const m = o as THREE.Mesh;
    if (m.isMesh && !m.userData?.codedWear) {
      const pos = m.geometry?.getAttribute?.("position");
      if (pos) {
        const toLocal = inv.clone().multiply(m.matrixWorld);
        const e = toLocal.elements;
        for (let i = 0; i < pos.count; i++) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const z = pos.getZ(i);
          visit(
            x * e[0]! + y * e[4]! + z * e[8]! + e[12]!,
            x * e[1]! + y * e[5]! + z * e[9]! + e[13]!,
            x * e[2]! + y * e[6]! + z * e[10]! + e[14]!,
          );
          any = true;
        }
      }
    }
    for (const c of o.children) walk(c);
  };
  walk(limb);
  return any;
}

/** A child pivot's y in the parent limb's local space (the elbowY idiom). */
function pivotLocalY(parent: THREE.Object3D, child: THREE.Object3D): number {
  parent.updateWorldMatrix(true, true);
  const inv = parent.matrixWorld.clone().invert();
  child.updateWorldMatrix(true, false);
  const we = child.matrixWorld.elements;
  const e = inv.elements;
  return we[12]! * e[1]! + we[13]! * e[5]! + we[14]! * e[9]! + e[13]!;
}

/**
 * Measure the whole-body wear envelope from a built humanoid rig. Bands are
 * placed from the rig's own proportions (shelfY = 0.525·s and hip-local
 * kneeY = −0.48·s·legLen are legLen/scale-independent identities of the
 * humanoid builder), so the measure is correct for any sex/build/scale.
 */
export function wearBodyFromRig(root: THREE.Object3D): WearBodySeat {
  const yoke = wearSeatFromRig(root);
  const hood = hoodSeatFromRig(root);
  const sleeve = sleeveSeatFromRig(root);
  const B = AVERAGE_MALE_BODY_RADII;

  // Effective scale: shelfY(torso-local) = 0.525·s on every build.
  const sEff = yoke.shelfY / 0.525 || 1.1;

  // ---- Torso: chest + hip bands (torso-local y = (nominal − 1.06)·s) ----
  let chestR = B.chestR;
  let chestZ = B.chestZ;
  let hipR = B.hipR;
  const torso = findLimb(root, "torso");
  if (torso) {
    const chestLo = (CHEST_BAND_NOMINAL[0] - 1.06) * sEff;
    const chestHi = (CHEST_BAND_NOMINAL[1] - 1.06) * sEff;
    const hipLo = (HIP_BAND_NOMINAL[0] - 1.06) * sEff;
    const hipHi = (HIP_BAND_NOMINAL[1] - 1.06) * sEff;
    let mChestR = 0;
    let mChestZ = 0;
    let mHipR = 0;
    const any = sweepLimbLocal(torso, (x, y, z) => {
      const r = Math.hypot(x, z);
      if (y >= chestLo && y <= chestHi) {
        if (r > mChestR) mChestR = r;
        if (z > mChestZ) mChestZ = z;
      }
      if (y >= hipLo && y <= hipHi && r > mHipR) mHipR = r;
    });
    if (any && mChestR > 0) {
      chestR = mChestR;
      chestZ = mChestZ;
      hipR = mHipR > 0 ? mHipR : hipR;
    }
  }

  // ---- Thigh (worse side) + knee pivot ----
  let thighR = B.thighR;
  let kneeY = B.kneeY;
  let thighMeasured = false;
  let kneeMeasured = false;
  for (const side of ["L", "R"] as const) {
    const hip = findLimb(root, `hip${side}`);
    const knee = findLimb(root, `knee${side}`);
    if (!hip) continue;
    let mR = 0;
    sweepLimbLocal(hip, (x, y, z) => {
      const r = Math.hypot(x, z);
      if (r > mR) mR = r;
    });
    if (mR > 0) {
      thighR = thighMeasured ? Math.max(thighR, mR) : mR;
      thighMeasured = true;
    }
    if (knee) {
      const ky = pivotLocalY(hip, knee);
      if (ky < -0.01) {
        kneeY = kneeMeasured ? Math.min(kneeY, ky) : ky;
        kneeMeasured = true;
      }
    }
  }

  // ---- Shin / ankle / boot (worse side, knee-local) ----
  // legLen from the measured knee: kneeY(hip-local) = −0.48·s·legLen.
  const legLen = kneeMeasured ? -kneeY / (0.48 * sEff) : 1;
  const ankleY = (HUMANOID_TUNING.ankleY - 0.58 * legLen) * sEff;
  let shinR = B.shinR;
  let ankleR = B.ankleR;
  let footLen = B.footLen;
  let legAny = false;
  for (const side of ["L", "R"] as const) {
    const knee = findLimb(root, `knee${side}`);
    if (!knee) continue;
    let mShin = 0;
    let mAnkle = 0;
    let footZMin = Infinity;
    let footZMax = -Infinity;
    const shinLo = ankleY + 0.04 * sEff;
    const ankleBand = 0.05 * sEff;
    const footHi = ankleY - 0.06 * sEff;
    sweepLimbLocal(knee, (x, y, z) => {
      const r = Math.hypot(x, z);
      if (y > shinLo && r > mShin) mShin = r;
      if (Math.abs(y - ankleY) <= ankleBand && r > mAnkle) mAnkle = r;
      if (y < footHi) {
        if (z < footZMin) footZMin = z;
        if (z > footZMax) footZMax = z;
      }
    });
    if (mShin > 0) {
      shinR = legAny ? Math.max(shinR, mShin) : mShin;
      if (mAnkle > 0) ankleR = legAny ? Math.max(ankleR, mAnkle) : mAnkle;
      if (footZMax > footZMin) {
        const len = footZMax - footZMin;
        footLen = legAny ? Math.max(footLen, len) : len;
      }
      legAny = true;
    }
  }

  return {
    yoke,
    hood,
    sleeve,
    chestR,
    chestZ,
    hipR,
    thighR,
    kneeY,
    shinR,
    ankleY,
    ankleR,
    footLen,
    sEff,
  };
}

/**
 * Pinned average-male (s=1.1) rig-MEASURED envelope — parity guard + the
 * authoring reference table. Lathe radii read ~3% under the formula profile
 * (the 10-segment polygon undershoots the true radius); capsule radii carry
 * the muscle-belly swell. Re-derive if HUMANOID_TUNING changes.
 */
export const AVERAGE_MALE_BODY = {
  chestR: 0.2534,
  chestZ: 0.1518,
  hipR: 0.2182,
  thighR: 0.1101,
  kneeY: -0.528,
  shinR: 0.0798,
  ankleY: -0.462,
  ankleR: 0.0495,
  footLen: 0.2607,
  sEff: 1.1,
} as const;

/** Stash seat + actual yoke (+ optional hood/sleeve) on the root after coded attach. */
export type CodedWearSeatState = {
  codedId: string;
  seat: WearSeat;
  /** Yoke height the builder actually used. */
  actualYokeY: number;
  /** Present when a hood was built. */
  hood?: HoodSeat;
  actualHoodApexY?: number;
  actualHoodShellR?: number;
  /** Present when sleeves were built from a measured arm. */
  sleeve?: SleeveSeat;
  actualSleeveR?: number;
};

export function recordCodedWearSeat(
  root: THREE.Object3D,
  codedId: string,
  actualYokeY: number,
  extra?: {
    hood?: { seat: HoodSeat; apexY: number; shellR: number };
    sleeve?: { seat: SleeveSeat; sleeveR: number };
  },
): void {
  const seat = wearSeatFromRig(root);
  const prev = (root.userData.codedWearSeat as CodedWearSeatState[] | undefined) ?? [];
  const entry: CodedWearSeatState = { codedId, seat, actualYokeY };
  if (extra?.hood) {
    entry.hood = extra.hood.seat;
    entry.actualHoodApexY = extra.hood.apexY;
    entry.actualHoodShellR = extra.hood.shellR;
  }
  if (extra?.sleeve) {
    entry.sleeve = extra.sleeve.seat;
    entry.actualSleeveR = extra.sleeve.sleeveR;
  }
  root.userData.codedWearSeat = [...prev, entry];
}
